import { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth.jsx'
import { cn } from '../lib/utils.js'
import AutoLogout from './AutoLogout.jsx'
import {
  RiDashboardLine,
  RiImageLine,
  RiGalleryLine,
  RiGroupLine,
  RiLogoutBoxRLine,
  RiExternalLinkLine,
  RiComputerLine,
  RiBookOpenLine,
  RiLayoutLine,
  RiMenuLine,
  RiCloseLine,
  RiSettings3Line,
} from 'react-icons/ri'

const navItems = [
  { to: '/',                  label: 'Beranda',           icon: RiDashboardLine,  roles: ['editor', 'admin', 'super_admin'] },
  { to: '/pratinjau-landing', label: 'Tampilan Landing',  icon: RiLayoutLine,     roles: ['editor', 'admin', 'super_admin'] },
  { to: '/tipe-gambar',       label: 'Tipe Gambar',       icon: RiGalleryLine,    roles: ['admin', 'super_admin'] },
  { to: '/gambar',            label: 'Gambar',            icon: RiImageLine,      roles: ['editor', 'admin', 'super_admin'] },
  { to: '/kelas',             label: 'Kelas',             icon: RiBookOpenLine,   roles: ['editor', 'admin', 'super_admin'] },
  { to: '/pengguna',          label: 'Pengguna',          icon: RiGroupLine,      roles: ['super_admin'] },
]

export default function Layout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const visibleNav = navItems.filter(
    (n) => !n.roles || user?.roles?.some((r) => n.roles.includes(r))
  )

  return (
    <div className="flex min-h-screen bg-slate-50">
      <AutoLogout idleMinutes={480} />

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
      )}

      {/* ── Sidebar (dark slate, Linear-style) ── */}
      <aside className={cn(
        'flex w-60 flex-col bg-slate-900 text-white',
        'fixed inset-y-0 left-0 z-50 transition-transform duration-200',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
        'md:static md:translate-x-0 md:z-auto'
      )}>

        {/* Logo / wordmark */}
        <div className="flex h-14 items-center justify-between px-5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-500 shadow-md">
              <RiSettings3Line className="text-sm text-white" />
            </div>
            <span className="text-sm font-semibold text-white tracking-tight leading-tight">
              LPK CMS
            </span>
          </div>
          <button className="md:hidden rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" onClick={() => setMobileOpen(false)}>
            <RiCloseLine className="text-base" />
          </button>
        </div>

        {/* Divider */}
        <div className="mx-4 h-px bg-slate-800" />

        {/* Nav */}
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-3">
          {visibleNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => cn(
                'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
                isActive
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              )}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Bottom — user info + logout */}
        <div className="border-t border-slate-800 px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
              {user?.name?.[0]?.toUpperCase() || 'A'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-semibold text-white">{user?.name}</div>
              <div className="truncate text-[11px] text-slate-400">{user?.roles?.[0] || 'editor'}</div>
            </div>
            <button
              onClick={handleLogout}
              title="Keluar"
              className="flex-shrink-0 rounded-md p-1.5 text-slate-500 hover:bg-slate-800 hover:text-white transition-colors"
            >
              <RiLogoutBoxRLine className="text-base" />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className="flex min-w-0 flex-1 flex-col">

        {/* Top bar — white, clean, subtle shadow */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-gray-200 bg-white px-6 shadow-sm">
          <div className="flex items-center gap-3">
            {/* Hamburger (mobile) */}
            <button
              className="md:hidden rounded-md p-1.5 text-gray-500 hover:bg-gray-100 transition-colors"
              onClick={() => setMobileOpen(true)}
            >
              <RiMenuLine className="text-base" />
            </button>
            <div className="hidden text-sm text-gray-400 md:block">
              Pengelolaan Konten
            </div>
          </div>

          {/* Right cluster */}
          <div className="flex items-center gap-2">
            <a
              href={import.meta.env.VITE_PUBLIC_SITE || '/'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <RiExternalLinkLine />
              <span className="hidden sm:inline">Lihat Website</span>
            </a>
            <button
              onClick={handleLogout}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-xs font-medium text-white hover:bg-blue-700 transition-colors"
            >
              <RiLogoutBoxRLine />
              <span className="hidden sm:inline">Keluar</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="mx-auto max-w-screen-xl px-6 py-8 md:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
